import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useQuery } from '@tanstack/react-query';
import { X } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePickerPopover } from '@/components/ui/date-picker-popover';
import { TimePickerPopover } from '@/components/ui/time-picker-popover';
import { TeamPicker } from '@/components/schedule/TeamPicker';
import { MatchDetailsSection } from '@/components/schedule/MatchDetailsSection';
import { listLocations } from '@/api/locations';
import { ACTIVITY_TYPES, type ActivityInput, type ActivityType } from '@/api/activities';
import { emptyMatchDetails, type MatchDetailsValue } from '@/api/fixtures';
import { CALLING_LEAD_VALUES, leadFromSelect, leadOptionKey, leadToSelect } from '@/lib/callingLead';
import type { TeamLite } from '@/hooks/useAsyncTeamSearch';

interface Props {
  slug: string;
  initial?: Partial<ActivityInput> & { id?: string };
  // Initial start (defaults to now rounded to next hour) when create.
  defaultStart?: Date;
  // match carries the opponent / home-away / competition / logo intent the
  // parent persists after the activity exists. Only sent when the type is
  // `match`; null otherwise.
  onSubmit: (input: ActivityInput, match: MatchDetailsValue | null) => Promise<unknown>;
  onCancel: () => void;
  submitting: boolean;
  error: string | null;
}

// ActivityFormModal — single-activity create / edit form. Renders as a
// div-based overlay (not a native <dialog>) — same pattern as
// TemplateSlotFormModal and LocationFormModal. The native dialog version
// races StrictMode's double-effect-invocation: cleanup calls el.close(),
// which fires the 'close' DOM event the sibling effect listens for, which
// calls onCancel() before the second mount cycle ever re-opens it.
//
// One team per activity — the searchable TeamPicker mirrors the template
// slot form. The API contract still carries team_ids as an array (for
// historical reasons); we submit a single-element array.
export function ActivityFormModal({
  slug,
  initial,
  defaultStart,
  onSubmit,
  onCancel,
  submitting,
  error,
}: Props) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  // The portal target for every Select in this form, held in STATE rather than a
  // ref. A `useRef` is null during the first render, and reading `.current` in
  // JSX bakes that null into the child's props — so on a freshly-opened modal the
  // first dropdown you touch portals to document.body instead of into this
  // overlay. A callback ref re-renders once the node exists, so `container` is
  // correct from the first paint onward.
  const [modalEl, setModalEl] = useState<HTMLDivElement | null>(null);

  const start = defaultStart ?? roundToNextHour(new Date());
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [activityType, setActivityType] = useState<ActivityType>(
    initial?.activity_type ?? 'training',
  );
  const [locationId, setLocationId] = useState<string>(initial?.location_id ?? '');
  const [zoneId, setZoneId] = useState<string>(initial?.zone_id ?? '');
  const [startsAt, setStartsAt] = useState<string>(toLocalInput(initial?.starts_at ?? start.toISOString()));
  const [endsAt, setEndsAt] = useState<string>(
    toLocalInput(initial?.ends_at ?? new Date(start.getTime() + 60 * 60 * 1000).toISOString()),
  );
  // The pickers edit the two halves; startsAt / endsAt stay the combined local
  // 'YYYY-MM-DDTHH:MM' string that validation and submit already speak.
  const { date: startDate, time: startTime } = splitLocal(startsAt);
  const { date: endDate, time: endTime } = splitLocal(endsAt);
  // Selected team — TeamPicker emits the full {id, name, color}, but on
  // mount we may only know the id (from the activity record being
  // edited). Pass a thin seed; the picker resolves the label via getTeam
  // and lifts the full record back via onChange.
  const initialTeamId = initial?.team_ids?.[0] ?? '';
  const [team, setTeam] = useState<TeamLite | null>(
    initialTeamId ? { id: initialTeamId, name: '', color: '' } : null,
  );
  const teamId = team?.id ?? '';
  const [publicVisibility, setPublicVisibility] = useState<boolean>(
    initial?.public_visibility ?? false,
  );
  // Org-wide (club-wide) visibility. Setting it is org-admin-only, but the
  // admin SPA is already owner/administrator-only (leaders work from the app —
  // see DashboardShell), so every user who reaches this form is an admin and
  // the toggle needs no extra per-field role gate. The backend enforces the
  // org-admin rule regardless.
  const [orgWide, setOrgWide] = useState<boolean>(initial?.org_wide ?? false);

  // Calling lead-time override for this activity. 'inherit' = use the team
  // category's default; 'off'; '0' = on create; 'N' = N days before. The actual
  // calling is sent by the worker — the form just records the choice.
  const [callingLead, setCallingLead] = useState<string>(
    leadToSelect(initial?.calling_lead_days ?? null),
  );

  // Match details — collected only while the type is `match`, persisted by the
  // parent after create (no activity id exists yet to attach a fixture to).
  const [match, setMatch] = useState<MatchDetailsValue>(emptyMatchDetails);

  const { data: locationsRes } = useQuery({
    queryKey: ['locations', slug, ''],
    queryFn: () => listLocations(token, slug),
  });

  const locationsList = locationsRes?.locations ?? [];
  const selectedLocation = locationsList.find((l) => l.id === locationId);
  const zonesForLocation = useMemo(
    () => selectedLocation?.zones ?? [],
    [selectedLocation],
  );

  // Reset zone when location changes — a zone belongs to one location.
  // Gated on `selectedLocation` being defined; without that, the effect
  // fires on mount before the locations query resolves, sees an empty
  // zonesForLocation, and wipes any zoneId seeded from `initial`.
  useEffect(() => {
    if (
      locationId &&
      selectedLocation &&
      !zonesForLocation.some((z) => z.id === zoneId)
    ) {
      setZoneId('');
    }
  }, [locationId, zoneId, zonesForLocation, selectedLocation]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Moving the start drags the end along by the same delta, so the duration
  // the user picked survives a date or time change — the common case is
  // "same 90-minute session, different day". Editing the end afterwards is
  // free; only the start pushes.
  function handleStartChange(next: string) {
    const prevStart = new Date(startsAt);
    const prevEnd = new Date(endsAt);
    const nextStart = new Date(next);
    setStartsAt(next);
    const duration = prevEnd.getTime() - prevStart.getTime();
    // Guard the incomplete/inverted cases (an empty half parses to NaN) —
    // shifting by NaN would wipe the end field.
    if (Number.isNaN(nextStart.getTime()) || Number.isNaN(duration) || duration <= 0) return;
    setEndsAt(toLocalInput(new Date(nextStart.getTime() + duration).toISOString()));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    if (!teamId) return;
    await onSubmit(
      {
        title: title.trim(),
        description: description.trim(),
        activity_type: activityType,
        location_id: locationId || null,
        zone_id: zoneId || null,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        team_ids: [teamId],
        public_visibility: publicVisibility,
        org_wide: orgWide,
        calling_lead_days: leadFromSelect(callingLead, true),
      },
      activityType === 'match' ? match : null,
    );
  }

  const validationError = (() => {
    if (!title.trim()) return t('schedule.validation.titleRequired');
    // The pickers can't produce a half-empty value, but joinLocal returns ''
    // if one ever did — catch it here so submit never calls toISOString() on
    // an Invalid Date.
    const s = new Date(startsAt).getTime();
    const e = new Date(endsAt).getTime();
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s) {
      return t('schedule.validation.timeOrdering');
    }
    if (!teamId) return t('schedule.validation.teamRequired');
    return null;
  })();

  return (
    <div
      ref={setModalEl}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/30 p-4"
    >
      <form
        onSubmit={handleSubmit}
        className="flex max-h-[90vh] w-[min(92vw,40rem)] flex-col overflow-hidden rounded-2xl bg-card text-foreground shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="font-heading text-lg font-bold">
            {initial?.id ? t('schedule.activity.title') : t('schedule.newActivity')}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('common.cancel')}
            onClick={onCancel}
          >
            <X weight="bold" />
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-[1fr_10rem] gap-3">
            <label className="block">
              <span className="font-heading text-sm font-semibold">
                {t('schedule.activity.title')}
              </span>
              <Input
                required
                autoFocus
                className="mt-1"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="font-heading text-sm font-semibold">
                {t('schedule.activity.type.label')}
              </span>
              <Select
                value={activityType}
                onValueChange={(v) => setActivityType(v as ActivityType)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent container={modalEl}>
                  {ACTIVITY_TYPES.map((k) => (
                    <SelectItem key={k} value={k}>
                      {t(`schedule.activity.type.option.${k}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>

          {/* Date + time as a click-to-pick pair, the platform-wide pattern
              (ScheduleDateTimeDialog, TemplateSlotFormModal, BroadcastEditor).
              A raw <input type="datetime-local"> renders mm/dd/yyyy and AM/PM
              for en-US browsers — see the 24h / ISO rules in CLAUDE.md. */}
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <span className="font-heading text-sm font-semibold">
                {t('schedule.activity.startsAt')}
              </span>
              <div className="mt-1 flex items-start gap-2">
                <DatePickerPopover
                  className="min-w-0 flex-1"
                  value={startDate}
                  onChange={(d) => handleStartChange(joinLocal(d, startTime))}
                  locale={i18n.language}
                  container={modalEl}
                />
                <TimePickerPopover
                  className="w-28 shrink-0"
                  value={startTime}
                  onChange={(tm) => handleStartChange(joinLocal(startDate, tm))}
                  ariaLabel={t('schedule.activity.startsAt')}
                />
              </div>
            </div>
            <div>
              <span className="font-heading text-sm font-semibold">
                {t('schedule.activity.endsAt')}
              </span>
              <div className="mt-1 flex items-start gap-2">
                <DatePickerPopover
                  className="min-w-0 flex-1"
                  value={endDate}
                  onChange={(d) => setEndsAt(joinLocal(d, endTime))}
                  locale={i18n.language}
                  min={startDate}
                  container={modalEl}
                />
                <TimePickerPopover
                  className="w-28 shrink-0"
                  value={endTime}
                  onChange={(tm) => setEndsAt(joinLocal(endDate, tm))}
                  ariaLabel={t('schedule.activity.endsAt')}
                />
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="font-heading text-sm font-semibold">
                {t('schedule.activity.location')}
              </span>
              {/* Radix Select doesn't allow empty-string SelectItem values,
                  so we encode "no location" as the __none__ sentinel and
                  map it back to '' on the way into form state.
                  container={modalEl} portals the dropdown INSIDE
                  this modal's z-[60] stacking context; without it Radix
                  portals to document.body and SelectContent's z-50 sits
                  behind the modal backdrop. */}
              <Select
                value={locationId === '' ? '__none__' : locationId}
                onValueChange={(v) => setLocationId(v === '__none__' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent container={modalEl}>
                  <SelectItem value="__none__">{t('schedule.activity.noLocation')}</SelectItem>
                  {locationsList.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-heading text-sm font-semibold">
                {t('schedule.activity.zone')}
              </span>
              <Select
                value={zoneId === '' ? '__none__' : zoneId}
                onValueChange={(v) => setZoneId(v === '__none__' ? '' : v)}
                disabled={!locationId || zonesForLocation.length === 0}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent container={modalEl}>
                  <SelectItem value="__none__">{t('schedule.activity.noZone')}</SelectItem>
                  {zonesForLocation.map((z) => (
                    <SelectItem key={z.id} value={z.id}>
                      {z.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>

          <div className="mt-4">
            <span className="font-heading text-sm font-semibold">
              {t('templates.slot.team')}
            </span>
            <TeamPicker
              slug={slug}
              value={team}
              onChange={setTeam}
              placeholder={t('templates.slot.teamPlaceholder')}
              searchPlaceholder={t('templates.slot.teamSearchPlaceholder')}
              emptyLabel={t('templates.slot.teamEmpty')}
            />
          </div>

          {activityType === 'match' ? (
            <div className="mt-4">
              <MatchDetailsSection slug={slug} value={match} onChange={setMatch} />
            </div>
          ) : null}

          <label className="mt-4 block">
            <span className="font-heading text-sm font-semibold">
              {t('schedule.activity.description')}
            </span>
            <textarea
              className="mt-1 w-full rounded-xl border border-border bg-background p-3 text-sm focus:border-primary focus:outline-none"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <label className="mt-4 flex items-start gap-3 rounded-xl border border-border bg-background p-3">
            <Checkbox
              className="mt-1"
              checked={publicVisibility}
              onChange={(e) => setPublicVisibility(e.target.checked)}
            />
            <span className="text-sm">
              <strong className="font-heading font-semibold">
                {t('schedule.activity.publicVisibility.label')}
              </strong>
              <p className="text-foreground/70">
                {t('schedule.activity.publicVisibility.help')}
              </p>
            </span>
          </label>

          <label className="mt-4 flex items-start gap-3 rounded-xl border border-border bg-background p-3">
            <Checkbox
              className="mt-1"
              checked={orgWide}
              onChange={(e) => setOrgWide(e.target.checked)}
            />
            <span className="text-sm">
              <strong className="font-heading font-semibold">
                {t('schedule.activity.orgWide.label')}
              </strong>
              <p className="text-foreground/70">
                {t('schedule.activity.orgWide.help')}
              </p>
            </span>
          </label>

          <label className="mt-4 block">
            <span className="font-heading text-sm font-semibold">
              {t('callings.sendToRoster.label')}
            </span>
            <Select value={callingLead} onValueChange={setCallingLead}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent container={modalEl}>
                <SelectItem value="inherit">{t('callings.lead.inherit')}</SelectItem>
                {CALLING_LEAD_VALUES.map((v) => (
                  <SelectItem key={v} value={leadToSelect(v)}>
                    {t(leadOptionKey(v))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-sm text-foreground/70">
              {t('callings.sendToRoster.help')}
            </p>
          </label>

          {(validationError || error) ? (
            <p
              role="alert"
              className="mt-4 rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error ?? validationError}
            </p>
          ) : null}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={submitting || !!validationError}>
            {submitting ? t('locations.form.saving') : t('locations.form.save')}
          </Button>
        </footer>
      </form>
    </div>
  );
}

// splitLocal / joinLocal bridge the combined local 'YYYY-MM-DDTHH:MM' form
// state and the separate ISO-date / 24h-time values the two pickers speak.
function splitLocal(v: string): { date: string; time: string } {
  const [date = '', rest = ''] = v.split('T');
  return { date, time: rest.slice(0, 5) };
}

function joinLocal(date: string, time: string): string {
  return date && time ? `${date}T${time}` : '';
}

function roundToNextHour(d: Date): Date {
  const r = new Date(d);
  r.setMinutes(0, 0, 0);
  r.setHours(r.getHours() + 1);
  return r;
}

// toLocalInput converts an ISO 8601 string to the canonical local
// YYYY-MM-DDTHH:MM form the form holds, in the browser's local TZ (so
// "starts at 18:00" stays at 18:00 wherever the user is).
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return [
    d.getFullYear(),
    '-',
    pad(d.getMonth() + 1),
    '-',
    pad(d.getDate()),
    'T',
    pad(d.getHours()),
    ':',
    pad(d.getMinutes()),
  ].join('');
}
